import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { MapBounds } from '../../types';
import { prefetchAround } from '../../services/tilePrefetch';

interface TilePrefetcherProps {
  /** Master-flagg (feature-flagg fra constants). */
  enabled: boolean;
  /** Leaflet-URL-maler for de aktive baselagene som skal prefetches. */
  urlTemplates: string[];
  /** Debounce etter kartflytt før prefetch starter (ms). */
  debounceMs?: number;
  /**
   * Auto-nav aktiv (appen følger GPS mens du kjører). Da prefetcher vi en korridor
   * FORAN i kjøreretningen — også på målt nett, med stramt tak — så tiles du er på
   * vei mot er varme før de ruller inn. Utenfor auto-nav: symmetrisk wifi-prefetch.
   */
  autoFollowActive?: boolean;
}

/** Færre fliser (stramt datatak) når vi prefetcher på målt nett under kjøring. */
const DRIVE_MAX_TILES = 30;

/**
 * Lytter på Leaflet `moveend` og prefetcher fliser. Ren sidekomponent (rendrer
 * ingenting) plassert inne i <MapContainer>. Hver ny bevegelse avbryter forrige runde.
 */
const TilePrefetcher: React.FC<TilePrefetcherProps> = ({
  enabled,
  urlTemplates,
  debounceMs,
  autoFollowActive = false,
}) => {
  const map = useMap();
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCenterRef = useRef<{ lat: number; lon: number } | null>(null);
  const templatesKey = urlTemplates.join('|');
  // Kjører oftere under kjøring (kartet flytter seg hyppig), roligere ellers.
  const effectiveDebounce = debounceMs ?? (autoFollowActive ? 400 : 800);

  useEffect(() => {
    if (!enabled || urlTemplates.length === 0) return;

    const run = () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const b = map.getBounds();
      const bounds: MapBounds = {
        latMin: b.getSouth(),
        latMax: b.getNorth(),
        lonMin: b.getWest(),
        lonMax: b.getEast(),
      };

      // Utled kjøreretning fra senter-delta siden forrige runde. Kun betydelige
      // forflytninger (> 25 % av viewport) regnes som retning; ellers symmetrisk.
      const center = { lat: (bounds.latMin + bounds.latMax) / 2, lon: (bounds.lonMin + bounds.lonMax) / 2 };
      const prev = lastCenterRef.current;
      lastCenterRef.current = center;
      let direction: { dLat: number; dLon: number } | undefined;
      if (autoFollowActive && prev) {
        const dLat = center.lat - prev.lat;
        const dLon = center.lon - prev.lon;
        const latSpan = bounds.latMax - bounds.latMin;
        const lonSpan = bounds.lonMax - bounds.lonMin;
        if (Math.abs(dLat) > 0.25 * latSpan || Math.abs(dLon) > 0.25 * lonSpan) {
          direction = { dLat, dLon };
        }
      }

      void prefetchAround(urlTemplates, bounds, map.getZoom(), ac.signal, {
        direction,
        allowMetered: autoFollowActive,
        maxTiles: autoFollowActive ? DRIVE_MAX_TILES : undefined,
      });
    };

    const onMoveEnd = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(run, effectiveDebounce);
    };

    map.on('moveend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
    // templatesKey dekker urlTemplates-innhold uten ny referanse hver render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, templatesKey, effectiveDebounce, autoFollowActive]);

  return null;
};

export default TilePrefetcher;
